import { useCallback, useEffect, useRef, useState } from "react";

import { currentAttribute, has, submit, whoAmI } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import { boxesOf, phaseOf } from "./show";

const RACER = "activity.press_race.racer";
const LOCKED = "activity.press_race.locked";
const RACING = "activity.press_race.racing";
const WON = "activity.press_race.won";

const NOBODY = "none";

/** The buzzer, the box it belongs to, and the two things a contestant sets: which box, and a name. */
export function ShowContestant({
  raceMount,
  seatMount,
  stageMount,
}: {
  raceMount: string;
  seatMount: string;
  stageMount: string;
}) {
  const race = useCanvasState(raceMount);
  const stage = useCanvasState(stageMount);
  const [me, setMe] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [refused, setRefused] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    whoAmI()
      .then((who) => {
        if (live) setMe(who.account_id);
      })
      .catch((error: unknown) => {
        if (live) setRefused(error instanceof Error ? error.message : "not signed in");
      });
    return () => {
      live = false;
    };
  }, []);

  const send = useCallback((mount: string, submission: string, value: string) => {
    setRefused(null);
    submit(mount, submission, value).catch((error: unknown) => {
      setRefused(error instanceof Error ? error.message : "that was refused");
    });
  }, []);

  const press = useCallback(() => send(raceMount, "press", "space"), [raceMount, send]);

  const boxes = boxesOf(stage.state);
  const mine = boxes.find((one) => one.who === me);
  const seated = mine !== undefined;

  useEffect(() => {
    if (!seated) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== "Space" && event.key !== " ") return;
      const on = event.target as HTMLElement | null;
      if (on !== null && on.tagName === "INPUT") return;
      event.preventDefault();
      press();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [press, seated]);

  const racer = (race.state?.entities ?? []).find((one) => one.id === me && has(one, RACER));
  const locked = racer !== undefined && has(racer, LOCKED);
  const racing = racer !== undefined && has(racer, RACING);
  const won = racer !== undefined && has(racer, WON);
  const wait = racer === undefined ? 0 : currentAttribute(racer, "ready_seconds");
  const phase = phaseOf(stage.state);

  const askedAt = useRef<number | null>(null);
  const [, tick] = useState(0);
  if (phase !== "asking") askedAt.current = null;
  else askedAt.current ??= performance.now();

  useEffect(() => {
    if (!locked) return undefined;
    const timer = window.setInterval(() => {
      tick((held) => held + 1);
    }, 100);
    return () => {
      window.clearInterval(timer);
    };
  }, [locked]);

  const since = askedAt.current === null ? 0 : (performance.now() - askedAt.current) / 1000;
  const left = Math.max(0, wait - since);

  let says = "Pick a box to get in the show.";
  if (seated && !racing && !won) says = "Waiting for the host to start a round.";
  if (seated && phase === "voting") says = "The crowd is picking. The question is next.";
  if (racing && locked) {
    says =
      mine?.slowed === true
        ? `Slowed, so you open ${left.toFixed(1)}s from now, a second after everyone else.`
        : `Opens in ${left.toFixed(1)}s.`;
  }
  if (racing && !locked) says = "Open. Hit space.";
  if (won) says = "You got there first.";

  return (
    <section style={page}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>{mine === undefined ? "No box yet" : mine.label}</h1>
        <strong style={{ fontSize: 30 }}>{mine === undefined ? "-" : Math.round(mine.points)}</strong>
      </header>

      <button
        type="button"
        onClick={press}
        disabled={!seated}
        style={{
          ...buzzer,
          background: won ? "#2e9e63" : racing && !locked ? "#d9455f" : "#2c3350",
          opacity: seated ? 1 : 0.4,
        }}
      >
        {won ? "IN" : "BUZZ"}
        <span style={{ display: "block", fontSize: 13, fontWeight: 500, opacity: 0.85, marginTop: 8 }}>
          {seated ? "or hit space" : "pick a box first"}
        </span>
      </button>

      <p style={{ textAlign: "center", minHeight: 22, opacity: 0.85 }}>{says}</p>

      <h2 style={{ fontSize: 15, opacity: 0.7, margin: "22px 0 8px" }}>Your box</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Seat label="None" on={!seated} onPick={() => send(seatMount, "sit", NOBODY)} />
        {boxes.map((box) => (
          <Seat
            key={box.place}
            label={`Box ${String(box.place)}`}
            note={box.taken && box.who !== me ? box.label : ""}
            on={mine?.place === box.place}
            disabled={box.taken && box.who !== me}
            onPick={() => send(seatMount, "sit", String(box.place))}
          />
        ))}
      </div>

      <h2 style={{ fontSize: 15, opacity: 0.7, margin: "22px 0 8px" }}>Your name</h2>
      <form
        style={{ display: "flex", gap: 8 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (typed.trim() !== "") send(seatMount, "name", typed.trim());
        }}
      >
        <input
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
          placeholder={mine?.name === "" ? "what to call your box" : (mine?.name ?? "")}
          maxLength={24}
          disabled={!seated}
          style={field}
        />
        <button type="submit" disabled={!seated} style={{ ...secondary, width: 100 }}>
          Set
        </button>
      </form>
      <p style={{ fontSize: 12, opacity: 0.55, marginTop: 6 }}>
        Letters and numbers only. Anything else becomes a dash.
      </p>

      <h2 style={{ fontSize: 15, opacity: 0.7, margin: "22px 0 8px" }}>On your box</h2>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", gap: 10 }}>
        <Effect on={mine?.charged === true} label="Charged" blurb="sooner, and double points" tint="#ffcf5c" />
        <Effect on={mine?.slowed === true} label="Slowed" blurb="a second later" tint="#5cd6ff" />
      </ul>

      {refused !== null && <p style={{ color: "#ff8f8f", marginTop: 16 }}>{refused}</p>}
    </section>
  );
}

function Seat({
  label,
  note = "",
  on,
  disabled = false,
  onPick,
}: {
  label: string;
  note?: string;
  on: boolean;
  disabled?: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      style={{
        ...secondary,
        flex: "1 1 22%",
        background: on ? "#5b6bff" : "#2c3350",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {label}
      {note !== "" && (
        <span style={{ display: "block", fontSize: 11, fontWeight: 500, opacity: 0.8 }}>{note}</span>
      )}
    </button>
  );
}

function Effect({
  on,
  label,
  blurb,
  tint,
}: {
  on: boolean;
  label: string;
  blurb: string;
  tint: string;
}) {
  return (
    <li
      style={{
        flex: 1,
        padding: "12px 14px",
        borderRadius: 10,
        background: on ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${on ? tint : "rgba(255,255,255,0.10)"}`,
        opacity: on ? 1 : 0.45,
      }}
    >
      <strong style={{ color: on ? tint : "inherit" }}>{label}</strong>
      <span style={{ display: "block", fontSize: 12, opacity: 0.75 }}>{blurb}</span>
    </li>
  );
}

const page: React.CSSProperties = {
  display: "block",
  maxWidth: 460,
  margin: "0 auto",
  padding: "32px 20px 60px",
  fontFamily: "system-ui, sans-serif",
};

const buzzer: React.CSSProperties = {
  width: "100%",
  margin: "24px 0 14px",
  padding: "46px 0",
  fontSize: 40,
  fontWeight: 800,
  letterSpacing: 2,
  color: "#f4f7ff",
  border: "none",
  borderRadius: 18,
  cursor: "pointer",
};

const secondary: React.CSSProperties = {
  padding: "13px 16px",
  fontSize: 15,
  fontWeight: 600,
  color: "#f4f7ff",
  background: "#2c3350",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
};

const field: React.CSSProperties = {
  flex: 1,
  padding: "12px 14px",
  fontSize: 15,
  color: "#f4f7ff",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 10,
};
